import { supabase } from '../client'
import type { CloudSection } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── SECTIONS (Cloud) ─────────────────────────────────────────── */

export async function getCloudSections(userId: string, since?: Date): Promise<CloudSection[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('sections')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('order', { ascending: true })

    if (since) {
      query = query.gte('updated_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      logSupabaseError('Get sections error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get sections error', error)
    return []
  }
}

/**
 * Get all cloud section IDs for a user (lightweight — only fetches id column).
 * Used for ID-set reconciliation to detect sections missing from local DB.
 */
export async function getCloudSectionIds(userId: string): Promise<string[]> {
  if (!supabase) return []

  try {
    const { data, error } = await supabase
      .from('sections')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(10000)

    if (error) {
      logSupabaseError('Get section IDs error', error)
      return []
    }

    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get section IDs error', error)
    return []
  }
}

/**
 * Get specific cloud sections by their IDs.
 * Used for targeted fetch of sections missing from local DB.
 */
export async function getCloudSectionsByIds(userId: string, ids: string[]): Promise<CloudSection[]> {
  if (!supabase || ids.length === 0) return []

  try {
    const { data, error } = await supabase
      .from('sections')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)

    if (error) {
      logSupabaseError('Get sections by IDs error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get sections by IDs error', error)
    return []
  }
}

export async function getDeletedCloudSections(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []

  try {
    let query = supabase
      .from('sections')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)

    if (since) {
      query = query.gte('deleted_at', since.toISOString())
    }

    const { data, error } = await query

    if (error) {
      logSupabaseError('Get deleted sections error', error)
      return []
    }

    return data || []
  } catch (error) {
    logSupabaseError('Get deleted sections error', error)
    return []
  }
}

export async function createCloudSection(
  userId: string,
  section: Omit<CloudSection, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<CloudSection | null> {
  if (!supabase) return null

  try {
    const { data, error } = await supabase
      .from('sections')
      .insert({
        ...section,
        user_id: userId,
      })
      .select()
      .single()

    if (error) {
      logSupabaseError('Create section error', error)
      return null
    }

    return data
  } catch (error) {
    logSupabaseError('Create section error', error)
    return null
  }
}

export async function updateCloudSection(
  sectionId: string,
  updates: Partial<Omit<CloudSection, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null

  try {
    let query = supabase
      .from('sections')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', sectionId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query
      .select('updated_at')
      .single()

    if (error) {
      logSupabaseError('Update section error', error)
      return null
    }

    return data
  } catch (error) {
    logSupabaseError('Update section error', error)
    return null
  }
}

export async function deleteCloudSection(sectionId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false

  try {
    let query = supabase
      .from('sections')
      .update({
        deleted_at: new Date().toISOString(),
      })
      .eq('id', sectionId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query

    if (error) {
      logSupabaseError('Delete section error', error)
      return false
    }

    return true
  } catch (error) {
    logSupabaseError('Delete section error', error)
    return false
  }
}
